---
title: How a night runs
description: The chain that turns a night of footage into a report, and the rules it lives by.
---

Every night, one script runs the same chain on the store's GPU machine. Each
stage reads what the previous one wrote, and each is allowed to refuse the
night rather than pass on bad data.

<PipelineDiagram />

## One script owns the order

Both cron and the control plane start a night through the same script,
`tools/nightly.sh`, so the stage order lives in exactly one place. The script
stops at the first failure. In order:

| Step        | What it does                                    | Why it is there                                                                                         |
| ----------- | ----------------------------------------------- | ------------------------------------------------------------------------------------------------------- |
| Lock        | Takes a lock file                               | Two nights must never run at once                                                                       |
| Config lint | Validates `store.yml` against a typed allowlist | Settings are machine-written by this app; a typo'd key must fail in seconds, not silently waste a night |
| Brokers up  | Starts Kafka and MQTT and waits for health      | Perception publishes to both                                                                            |
| Fetch       | Stages the night to local disk                  | Everything downstream reads local files                                                                 |
| Perception  | The DeepStream run                              | Produces the tracking stream                                                                            |
| Analytics   | Replays the tracking stream                     | Produces the report                                                                                     |
| Review      | Optional VLM pass over flagged events           | Non-fatal: the report is already complete without it, so a down model server must not fail the night    |

Each night gets a session id, like `2026-08-18-store-001`. It names the
night's Kafka topic, which is what lets two different nights coexist without
mixing their data.

## The two rules everything follows

**Frame N of every camera shows the same instant.** Cameras are paired frame
by frame, by position, never by timestamp. Cross-camera identity is
negotiated on frame numbers, and the matcher searches only one frame either
side. A walking person covers about 1.4 meters per second, so every second
of camera skew becomes 1.4 meters of position error. This is why the intake
gates are strict and why a refused night is a feature, not a bug.

**Fail loudly at the stage that knows.** A wrong detector setting does not
crash; it silently returns garbage. A bumped camera does not crash; it
silently shifts every position. So every stage checks what only it can check
and refuses to continue, because an error message at 2 a.m. is cheaper than
a plausible-looking report that is wrong.

## Symptom to stage

When a night misbehaves, this table names the stage to look at first:

| Symptom                                          | Look at                                                                              |
| ------------------------------------------------ | ------------------------------------------------------------------------------------ |
| Night refused before processing                  | [Fetch and intake](/docs/pipeline/fetch): the session gates name the failed check    |
| No detections at all                             | [Perception](/docs/pipeline/perception): detector config or engine build             |
| Same person, two identities (or two people, one) | [Perception](/docs/pipeline/perception): the tracker's match score, then calibration |
| Nothing on the Kafka topic                       | [Perception](/docs/pipeline/perception): the broker sink                             |
| Analytics exits with "no data on the topic"      | The perception run before it, or the topic name                                      |
| Counts plausible but dwell strange               | The tracker. Dwell breaks before counts do                                           |
| A camera flagged as quiet                        | The camera itself: bumped, dirty or offline. Recalibrate after touching it           |

The stage pages that follow walk through each part in detail:
[Fetch and intake](/docs/pipeline/fetch),
[Perception](/docs/pipeline/perception),
[Analytics](/docs/pipeline/analytics) and
[Review](/docs/pipeline/review).
